// ---- Pose blending (client-visual) ----
// Kills the hard animation pops: the attack clip fades IN at swing start and OUT
// as it ends (instead of snapping gait/stance → clip → gait/stance), and the
// carry↔ready stance switch eases instead of popping. Purely presentational —
// the deterministic combat sim (src/sim/combat.ts) is pose-free, so nothing here
// touches a number the parity/snapshot tests assert, and there is NO wire change
// (attackT / atkSeq / combatReady already ride the snapshot; the blend is derived
// locally from them). Applied inside pose.ts's applyWeaponPose, the ONE choke
// point every rig (player, NPCs, remotes) funnels through.
//
// Allocation-free: euler-component lerp in place, matching the existing idiom
// (sampleChannel already lerps euler components; gait uses a scalar ease). Quat
// slerp is overkill for the short (~10-15%) fade windows on mostly single-axis
// joint rotations.

import { JOINT_CHANNELS, sampleChannel, type AttackClip, type PoseChannel, type StancePose, type Vec3 } from "./clips.ts";
import type { RigHandle } from "../model/rig.ts";

/** Fade windows (seconds). Mutable so the studio transition panel can scrub them
 *  live. Client-visual — deliberately NOT in the pure sim's tunables. */
export const BLEND_TUNING = { attackFadeIn: 0.08, attackFadeOut: 0.10, stanceFade: 0.15 };

export function lerp(a: number, b: number, f: number): number { return a + (b - a) * f; }

/** Blend weight for the attack clip at normalized time `k` (0→1): ramps 0→1 over
 *  the fade-in, holds at 1, ramps 1→0 over the fade-out. `dur` is the clip length
 *  in seconds (the fades are absolute-time, so short clips don't over-fade). */
export function attackEnvelope(k: number, dur: number): number {
  const fin = dur > 0 ? BLEND_TUNING.attackFadeIn / dur : 0;
  const fout = dur > 0 ? BLEND_TUNING.attackFadeOut / dur : 0;
  const rin = fin > 0 ? Math.min(1, k / fin) : 1;
  const rout = fout > 0 ? Math.min(1, (1 - k) / fout) : 1;
  return Math.max(0, Math.min(1, rin * rout));
}

/** Sample a clip onto the rig, but each animated joint channel is blended from its
 *  CURRENT rig euler (the stance/gait pose already written this frame) toward the
 *  clip value by weight `w`. At w=1 it's a plain absolute write; at w=0 the
 *  channel keeps the underlying pose. Channels the clip doesn't define are left
 *  alone. (This subsumes the old absolute applyClip — it IS w=1.) */
export function applyClipBlended(rig: RigHandle, clip: AttackClip, k: number, w: number): void {
  for (const ch of JOINT_CHANNELS) {
    const g = rig[ch];
    if (!g) continue;
    const v = sampleChannel(clip, ch, k);
    if (!v) continue;
    if (w >= 1) { g.rotation.set(v[0], v[1], v[2]); continue; }
    g.rotation.x = lerp(g.rotation.x, v[0], w);
    g.rotation.y = lerp(g.rotation.y, v[1], w);
    g.rotation.z = lerp(g.rotation.z, v[2], w);
  }
}

/** The union of joint channels two stances touch (for the stance-ease capture). */
export function stanceChannels(a?: StancePose, b?: StancePose): PoseChannel[] {
  const set = new Set<PoseChannel>();
  for (const p of [a, b]) if (p) for (const ch of JOINT_CHANNELS) if (p[ch] !== undefined) set.add(ch);
  return [...set];
}

/** Snapshot the pose to ease FROM when the stance flips: the stance we're leaving
 *  for each channel, or the current rig euler (gait) where that stance is silent. */
export function captureStanceFrom(
  rig: RigHandle, leaving: StancePose | undefined, channels: readonly PoseChannel[], out: Record<string, Vec3>,
): void {
  for (const ch of channels) {
    const g = rig[ch];
    out[ch] = leaving?.[ch]
      ? [leaving[ch]![0], leaving[ch]![1], leaving[ch]![2]]
      : g ? [g.rotation.x, g.rotation.y, g.rotation.z] : [0, 0, 0];
  }
}

/** Ease the rig from a captured `from` pose toward the target stance by `sb`
 *  (0→1). Channels the target stance doesn't define settle to the current gait
 *  euler (read before the write), so a dropped channel returns to the walk pose.
 *
 *  `rest` (optional, the body-pose exit path): the pristine per-channel eulers
 *  captured BEFORE the layer ever wrote the joint. The gait rewrites rotation.x
 *  each frame ahead of this pass, so live x is a valid settle target — but it
 *  does NOT write y/z on most joints (hips/knees/elbows), so live y/z there is
 *  this layer's OWN previous write: without `rest`, an exit fade converges onto
 *  that residue and freezes it in (legs stuck together after standing from a
 *  sit). With `rest`, undefined channels settle x→live gait, y/z→rest. */
export function applyStanceBlended(
  rig: RigHandle, from: Record<string, Vec3>, toPose: StancePose, sb: number,
  rest?: Record<string, Vec3>,
): void {
  for (const ch of JOINT_CHANNELS) {
    const g = rig[ch];
    const f = from[ch];
    if (!g || !f) continue;
    const r = rest?.[ch];
    const to = toPose[ch]
      ?? (r ? [g.rotation.x, r[1], r[2]] : [g.rotation.x, g.rotation.y, g.rotation.z]);
    g.rotation.set(lerp(f[0], to[0], sb), lerp(f[1], to[1], sb), lerp(f[2], to[2], sb));
  }
}
